import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tag, Sparkle, Ticket, Prohibit, CheckCircle } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";

const EMPTY = {
  name: "", code: "", discount_type: "percent", discount_value: "",
  duration: "once", duration_in_months: "", max_redemptions: "",
  expires_at: "", first_time_only: false, min_amount: "",
};

function randomCode(prefix = "SAVE") {
  const n = Math.floor(Math.random() * 90 + 10);
  return `${prefix}${n}`;
}

export default function AdminPromotions() {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);

  const { data, isError, error } = useQuery({
    queryKey: ["admin-promotions"],
    queryFn: async () => (await api.get("/admin/promotions")).data,
    retry: false,
  });
  const promotions = data?.promotions || [];
  const notConfigured = error?.response?.status === 503;

  const create = useMutation({
    mutationFn: async () => (await api.post("/admin/promotions", {
      name: form.name || form.code,
      code: form.code,
      discount_type: form.discount_type,
      discount_value: parseFloat(form.discount_value) || 0,
      duration: form.duration,
      duration_in_months: form.duration === "repeating" ? (parseInt(form.duration_in_months) || 0) : null,
      max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      first_time_only: form.first_time_only,
      min_amount: form.min_amount ? parseFloat(form.min_amount) : null,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-promotions"] });
      toast.success(`Promo code ${form.code.toUpperCase()} created — live at checkout.`);
      setForm(EMPTY);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const toggle = useMutation({
    mutationFn: async ({ id, active }) => api.post(`/admin/promotions/${id}/${active ? "deactivate" : "activate"}`),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ["admin-promotions"] });
      toast.success(v.active ? "Promo code deactivated." : "Promo code reactivated.");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const flashSale = () => setForm({ ...EMPTY, name: "Flash Sale", code: randomCode("FLASH"), discount_type: "percent", discount_value: "20", duration: "once" });

  const submit = (e) => { e.preventDefault(); create.mutate(); };

  const discountLabel = (p) => {
    const c = p.coupon || {};
    if (c.percent_off) return `${c.percent_off}% off`;
    if (c.amount_off) return `$${c.amount_off} off`;
    return "—";
  };
  const durationLabel = (c) => {
    if (c.duration === "repeating") return `${c.duration_in_months} mo`;
    if (c.duration === "forever") return "forever";
    return "once";
  };

  return (
    <div data-testid="admin-promotions-page" className="grid lg:grid-cols-2 gap-6">
      {/* Create form */}
      <Card className="p-6 bg-card border-border h-fit">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-primary" weight="bold" />
            <h3 className="text-lg font-black">Create promo code</h3>
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={flashSale} data-testid="promo-flash-sale" className="rounded-md">
            <Sparkle className="h-4 w-4 mr-1.5" weight="fill" /> Flash sale
          </Button>
        </div>

        {notConfigured ? (
          <p className="text-sm text-amber-400" data-testid="promo-not-configured">
            Stripe is not configured yet. Add your Stripe keys to manage promotions.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Code (shown at checkout)</Label>
                <Input required data-testid="promo-code" className="mt-1.5 bg-secondary/50 uppercase"
                  value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="LAUNCH20" />
              </div>
              <div>
                <Label>Internal name</Label>
                <Input data-testid="promo-name" className="mt-1.5 bg-secondary/50"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Launch promo" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Discount type</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger data-testid="promo-discount-type" className="mt-1.5 bg-secondary/50"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage (%)</SelectItem>
                    <SelectItem value="amount">Fixed amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{form.discount_type === "percent" ? "Percent off" : "Amount off (CAD)"}</Label>
                <Input required type="number" step="0.01" data-testid="promo-discount-value" className="mt-1.5 bg-secondary/50"
                  value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                  placeholder={form.discount_type === "percent" ? "20" : "5.00"} />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Applies for</Label>
                <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v })}>
                  <SelectTrigger data-testid="promo-duration" className="mt-1.5 bg-secondary/50"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">First payment only</SelectItem>
                    <SelectItem value="repeating">Several months</SelectItem>
                    <SelectItem value="forever">Forever</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.duration === "repeating" && (
                <div>
                  <Label>Number of months</Label>
                  <Input type="number" data-testid="promo-months" className="mt-1.5 bg-secondary/50"
                    value={form.duration_in_months} onChange={(e) => setForm({ ...form, duration_in_months: e.target.value })} placeholder="3" />
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Max redemptions (optional)</Label>
                <Input type="number" data-testid="promo-max-redemptions" className="mt-1.5 bg-secondary/50"
                  value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} placeholder="Unlimited" />
              </div>
              <div>
                <Label>Expires (optional)</Label>
                <Input type="date" data-testid="promo-expires" className="mt-1.5 bg-secondary/50"
                  value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
              </div>
            </div>

            <div>
              <Label>Minimum order amount (optional, CAD)</Label>
              <Input type="number" step="0.01" data-testid="promo-min-amount" className="mt-1.5 bg-secondary/50"
                value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} placeholder="No minimum" />
            </div>

            <div className="flex items-center justify-between rounded-md bg-secondary/40 px-3 py-2.5">
              <span className="text-sm">New customers only</span>
              <Switch checked={form.first_time_only} onCheckedChange={(v) => setForm({ ...form, first_time_only: v })} data-testid="promo-first-time" />
            </div>

            <Button type="submit" disabled={create.isPending} data-testid="promo-create" className="rounded-md font-semibold">
              <Ticket className="h-4 w-4 mr-1.5" weight="bold" /> {create.isPending ? "Creating…" : "Create promo code"}
            </Button>
          </form>
        )}
      </Card>

      {/* List */}
      <div>
        <h3 className="text-base font-bold mb-3">Active & past promotions</h3>
        <div className="space-y-3" data-testid="promo-list">
          {isError && !notConfigured && <p className="text-sm text-destructive">Failed to load promotions.</p>}
          {!isError && promotions.length === 0 && <p className="text-sm text-muted-foreground">No promo codes yet. Create your first above.</p>}
          {promotions.map((p) => (
            <Card key={p.id} className={`p-4 bg-card border-border ${p.active ? "" : "opacity-60"}`} data-testid={`promo-${p.code}`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold tracking-wide">{p.code}</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${p.active ? "bg-emerald-500/15 text-emerald-400" : "bg-secondary text-muted-foreground"}`}>
                    {p.active ? "Active" : "Inactive"}
                  </span>
                </div>
                <button data-testid={`promo-toggle-${p.code}`} onClick={() => toggle.mutate({ id: p.id, active: p.active })}
                  disabled={toggle.isPending}
                  className={`transition-colors duration-200 disabled:opacity-40 ${p.active ? "text-muted-foreground hover:text-destructive" : "text-muted-foreground hover:text-emerald-400"}`}
                  title={p.active ? "Deactivate" : "Reactivate"}>
                  {p.active ? <Prohibit className="h-5 w-5" weight="bold" /> : <CheckCircle className="h-5 w-5" weight="bold" />}
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{discountLabel(p)}</span>
                <span>· {durationLabel(p.coupon || {})}</span>
                <span>· Redeemed {p.times_redeemed}{p.max_redemptions ? ` / ${p.max_redemptions}` : ""}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {p.first_time_only && <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">New customers</span>}
                {p.min_amount && <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">Min ${p.min_amount}</span>}
                {p.expires_at && <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">Expires {format(new Date(p.expires_at), "MMM d, yyyy")}</span>}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
